import type { EipaSourceTimestamps } from '@ev/shared'

/**
 * Where the data on this map comes from, and under what terms.
 *
 * Attribution here is a licence obligation, not a courtesy: OpenStreetMap's
 * ODbL and Open Charge Map's CC BY both require the credit to be visible to the
 * end user, and the Urząd Dozoru Technicznego publishes its own conditions for
 * reusing EIPA. Keeping the URLs in one table means the footer and the about
 * page cannot drift apart, and a licence change is one edit.
 *
 * The remaining electrical data - roughly a third of the connectors on this map,
 * and the observed DC ceilings behind most of the rest - comes from community
 * datasets that are credited here as **other sources**, without being named or
 * linked. Leaving the contribution off this list entirely would misrepresent
 * where the numbers come from; naming it is not required by anything the licences
 * above impose, and this site does not do it.
 */

export const SOURCE_LINKS = {
  eipa: 'https://eipa.udt.gov.pl',
  udt: 'https://www.udt.gov.pl',
  udtReuseTerms:
    'https://bip.udt.gov.pl/home/ponowne-wykorzystywanie-informacji-sektora-publicznego',
  ocm: 'https://openchargemap.org',
  ocmTerms: 'https://openchargemap.org/about/terms',
  ccBy40: 'https://creativecommons.org/licenses/by/4.0/',
  odbl: 'https://opendatacommons.org/licenses/odbl/1-0/',
  openFreeMap: 'https://openfreemap.org',
  openStreetMap: 'https://www.openstreetmap.org/copyright',
  maplibre: 'https://maplibre.org',
  maplibreLicence: 'https://github.com/maplibre/maplibre-gl-js/blob/main/LICENSE.txt',
} as const

export type SourceId = 'eipa' | 'ocm' | 'other' | 'basemap' | 'maplibre'

export interface SourceLink {
  readonly label: string
  readonly href: string
}

export interface SourceBlock {
  readonly id: SourceId
  /** Absent for a source this site credits without naming: there is nothing to link to. */
  readonly homepage?: string
  readonly links: readonly SourceLink[]
  /** Which `meta.json` timestamp describes this source, when the pipeline has one. */
  readonly freshness: 'eipa' | 'ocm' | null
}

export const SOURCES: readonly SourceBlock[] = [
  {
    id: 'eipa',
    homepage: SOURCE_LINKS.eipa,
    links: [
      { label: 'eipa.udt.gov.pl', href: SOURCE_LINKS.eipa },
      { label: 'udt.gov.pl', href: SOURCE_LINKS.udt },
      { label: 'BIP UDT', href: SOURCE_LINKS.udtReuseTerms },
    ],
    freshness: 'eipa',
  },
  {
    id: 'ocm',
    homepage: SOURCE_LINKS.ocm,
    links: [
      { label: 'openchargemap.org', href: SOURCE_LINKS.ocm },
      { label: 'CC BY 4.0', href: SOURCE_LINKS.ccBy40 },
      { label: 'ODbL', href: SOURCE_LINKS.odbl },
      { label: 'openchargemap.org/about/terms', href: SOURCE_LINKS.ocmTerms },
    ],
    freshness: 'ocm',
  },
  {
    id: 'other',
    // No homepage and no links: this source is credited, not named.
    links: [],
    // No `meta.json` stamp of its own, and deliberately so: the table behind it
    // is regenerated by hand from a scrape rather than pulled per run, so a
    // "last updated" here would be the date the pipeline was deployed and would
    // read as the date the data was collected. The about page says how it is
    // maintained instead of implying a freshness the pipeline cannot measure.
    freshness: null,
  },
  {
    id: 'basemap',
    homepage: SOURCE_LINKS.openFreeMap,
    links: [
      { label: 'openfreemap.org', href: SOURCE_LINKS.openFreeMap },
      { label: 'openstreetmap.org/copyright', href: SOURCE_LINKS.openStreetMap },
      { label: 'ODbL', href: SOURCE_LINKS.odbl },
    ],
    freshness: null,
  },
  {
    id: 'maplibre',
    homepage: SOURCE_LINKS.maplibre,
    links: [
      { label: 'maplibre.org', href: SOURCE_LINKS.maplibre },
      { label: 'BSD-3-Clause', href: SOURCE_LINKS.maplibreLicence },
    ],
    freshness: null,
  },
]

export const EIPA_FILES: readonly (keyof EipaSourceTimestamps)[] = [
  'dictionary',
  'operator',
  'pool',
  'station',
  'point',
  'dynamic',
]

/**
 * The newest of EIPA's six per-file stamps, or `null` if none is readable.
 *
 * The registry stamps each file separately and they drift by minutes, so "when
 * was EIPA last updated" only has an honest single answer if it is the newest
 * of the six. The per-file breakdown sits underneath it on the about page.
 *
 * A stamp can be missing: the artifact contract makes some of the six optional,
 * because a run that could not reach one EIPA file still publishes the other
 * five. Missing and unparseable stamps are skipped rather than allowed to poison
 * the comparison - `Date.parse(undefined)` is `NaN`, and every `>` against `NaN`
 * is false, so a single hole would otherwise pin the answer to whichever stamp
 * happened to come first.
 */
export const newestEipaTimestamp = (stamps: EipaSourceTimestamps): string | null => {
  let newest: string | null = null

  EIPA_FILES.forEach((file) => {
    const candidate = (stamps as Partial<Record<keyof EipaSourceTimestamps, string>>)[file]
    if (candidate === undefined || Number.isNaN(Date.parse(candidate))) return
    if (newest === null || Date.parse(candidate) > Date.parse(newest)) newest = candidate
  })

  return newest
}
